import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories } from "@/mocks/categories";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

const services = [
  {
    icon: "ri-layout-grid-line",
    title: "分类浏览",
    desc: "按最新上线、经典佳作、高评分、情欲佳作、剧情片与悬疑惊悚等分类系统整理，方便按主题查找。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选",
    desc: "支持按类型、年份、评分与排序方式组合筛选，快速缩小范围，精准定位目标作品。",
  },
  {
    icon: "ri-file-text-line",
    title: "剧情详介",
    desc: "每部作品提供剧情简介、导演与主演、上映年份、时长及关键词标签等资料信息。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "本地收藏",
    desc: "无需登录即可收藏感兴趣的作品，收藏记录保存在本机浏览器中，刷新页面依然保留。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 韩国三级电影",
    description:
      "了解韩国三级电影网站的定位与服务：专注韩国 R 级影视作品的资料整理，提供分类浏览、多维筛选与剧情详介，并说明内容版权与联系方式。",
    keywords: "关于韩国三级电影,韩国电影资料站,内容版权声明,影视资源介绍",
  });

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于" }]} />

        <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="flex flex-col justify-center p-6 md:p-10">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-information-line" aria-hidden="true" />
                关于本站
              </span>
              <h1 className="mt-4 text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
                一个专注韩国 R 级影视作品的资料整理站
              </h1>
              <p className="mt-4 text-sm leading-7 text-foreground-600">
                韩国三级电影以「专业资料」为定位，围绕韩国 R 级与成人向文艺作品进行系统整理。我们希望为影迷提供一个清晰、易读、可筛选的资料库，让查找剧情、演职员信息与同类作品变得简单高效。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                本站不提供在线播放、下载或任何第三方接口对接，站内片名、人物与评分均为演示数据，仅用于展示页面结构与交互效果。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
                >
                  <i className="ri-home-5-line text-base" aria-hidden="true" />
                  开始浏览
                </Link>
                <Link
                  to="/search"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-search-line text-base" aria-hidden="true" />
                  搜索影片
                </Link>
              </div>
            </div>

            <div className="relative min-h-[240px] md:min-h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Editorial%20flatlay%20of%20a%20vintage%20film%20archive%20desk%2C%20stacked%20movie%20stills%2C%20film%20reels%20and%20a%20notebook%2C%20warm%20deep%20red%20and%20charcoal%20tones%2C%20soft%20directional%20light%2C%20refined%20professional%20mood%2C%20no%20text&width=1200&height=900&seq=kfilm-about-01&orientation=landscape"
                alt="韩国三级电影资料整理工作台"
                title="韩国三级电影 - 资料整理"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-950 md:text-xl">
            <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
            内容与服务
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service) => (
              <div
                key={service.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${service.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{service.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <section className="lg:col-span-2">
            <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-950 md:text-xl">
              <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
              版权声明
            </h2>
            <div className="mt-4 space-y-3 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-7 text-foreground-600 md:p-6">
              <p>
                本站所展示的片名、简介、演职员与评分为演示数据，仅用于产品功能展示，不代表任何真实影视作品的实际信息。
              </p>
              <p>
                本站不提供、不存储、不传播任何影片文件，不提供在线播放、下载或外链跳转服务；如涉及第三方内容权益，请通过下方方式联系我们处理。
              </p>
              <p>
                站内文字内容为原创整理排版，未经许可请勿用于商业用途。使用者应遵守所在地区的法律法规，合理安排浏览内容。
              </p>
            </div>
          </section>

          <section>
            <h2 className="flex items-center gap-3 text-lg font-bold text-foreground-950 md:text-xl">
              <span className="h-5 w-1 rounded-full bg-primary-500" aria-hidden="true" />
              联系我们
            </h2>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <ul className="space-y-3 text-sm text-foreground-700">
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line mt-0.5 text-base text-primary-600" aria-hidden="true" />
                  <span>
                    反馈邮箱：
                    <a
                      href="mailto:contact@kfilm-demo.example"
                      className="text-primary-600 transition-colors duration-200 hover:text-primary-700"
                    >
                      contact@kfilm-demo.example
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-time-line mt-0.5 text-base text-primary-600" aria-hidden="true" />
                  <span>回复时间：工作日 10:00 - 18:00</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line mt-0.5 text-base text-primary-600" aria-hidden="true" />
                  <span>服务范围：线上资料站（演示）</span>
                </li>
              </ul>
              <p className="mt-4 text-xs leading-relaxed text-foreground-500">
                以上联系方式为演示信息，实际使用时可替换为你的真实联系方式。
              </p>
            </div>
          </section>
        </div>

        <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="text-base font-bold text-foreground-950">快速进入分类</h2>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
              >
                <i className={`${cat.icon} text-base text-primary-500`} aria-hidden="true" />
                {cat.name}
              </Link>
            ))}
            <span className="flex items-center whitespace-nowrap rounded-full bg-secondary-100 px-4 py-2 text-sm font-medium text-secondary-900">
              已收录 {movies.length} 部演示作品
            </span>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}